import * as yup from 'yup'
import i18next from 'i18next'

type DescriptionTextSource = 'manual' | 'extracted' | 'ai_generated'

export const getOfferFormSchema = (t: (key: string, opts?: any) => string) => yup.object({
  job_title: yup
    .string()
    .required(t('createOffer.validation.jobTitleRequired'))
    .min(3, t('createOffer.validation.jobTitleMin'))
    .max(100, t('createOffer.validation.jobTitleMax')),
  
  department: yup
    .string()
    .nullable(),
  
  contract_type: yup
    .string()
    .nullable(),
  
  experience_level: yup
    .string()
    .nullable(),
  
  remote_allowed: yup
    .boolean(),
  
  description: yup
    .string()
    .nullable(),
  description_text: yup
    .string()
    .required(t('createOffer.validation.descriptionRequired'))
    .min(50, t('createOffer.validation.descriptionMin'))
    .test('not-html-only', t('createOffer.validation.descriptionHtmlOnly'), function(value) {
      if (!value) return true
      const textContent = value.replace(/<[^>]*>/g, '').trim()
      return textContent.length >= 50
    }),
  description_text_source: yup
    .mixed<DescriptionTextSource>()
    .oneOf(['manual', 'extracted', 'ai_generated'])
    .default('manual'),
  
  requirements: yup
    .array()
    .of(yup.string().min(1, t('createOffer.validation.requirementItemMin')))
    .nullable()
    .default([])
    .test('no-empty-requirements', t('createOffer.validation.requirementsRequired'), function(requirements) {
      if (!requirements || requirements.length === 0) return true
      return requirements.every(req => req && req.trim().length > 0)
    }),
  
  benefits: yup
    .array()
    .of(yup.string().min(1, t('createOffer.validation.benefitItemMin')))
    .nullable()
    .default([])
    .test('no-empty-benefits', t('createOffer.validation.benefitsRequired'), function(benefits) {
      if (!benefits || benefits.length === 0) return true
      return benefits.every(benefit => benefit && benefit.trim().length > 0)
    }),

  education_level: yup
    .string()
    .nullable(),

  languages: yup
    .array()
    .of(yup.object({
      language: yup.string().min(1, t('createOffer.validation.languageNameRequired')),
      level: yup.string().min(1, t('createOffer.validation.languageLevelRequired'))
    }))
    .nullable()
    .default([])
    .test('no-empty-languages', t('createOffer.validation.languagesRequired'), function(languages) {
      if (!languages || languages.length === 0) return true
      return languages.every(lang => 
        lang && lang.language && lang.language.trim().length > 0 && 
        lang.level && lang.level.trim().length > 0
      )
    }),

  certifications: yup
    .array()
    .of(yup.string().min(1, t('createOffer.validation.certificationItemMin')))
    .nullable()
    .default([])
    .test('no-empty-certifications', t('createOffer.validation.certificationsRequired'), function(certifications) {
      if (!certifications || certifications.length === 0) return true
      return certifications.every(cert => cert && cert.trim().length > 0)
    }),

  main_missions: yup
    .array()
    .of(yup.string().min(1, t('createOffer.validation.missionItemMin')))
    .nullable()
    .default([])
    .test('no-empty-missions', t('createOffer.validation.missionsRequired'), function(missions) {
      if (!missions || missions.length === 0) return true
      return missions.every(mission => mission && mission.trim().length > 0)
    }),

  soft_skills: yup
    .array()
    .of(yup.string().min(1, t('createOffer.validation.softSkillItemMin')))
    .nullable()
    .default([])
    .test('no-empty-soft-skills', t('createOffer.validation.softSkillsRequired'), function(softSkills) {
      if (!softSkills || softSkills.length === 0) return true
      return softSkills.every(skill => skill && skill.trim().length > 0)
    }),
  
  ai_matching: yup.object({
    hard_skills: yup
      .number()
      .required()
      .min(0, t('createOffer.validation.aiWeightPositive'))
      .max(10, t('createOffer.validation.aiWeightMax')),
    soft_skills: yup
      .number()
      .required()
      .min(0, t('createOffer.validation.aiWeightPositive'))
      .max(10, t('createOffer.validation.aiWeightMax')),
    experience_years: yup
      .number()
      .required()
      .min(0, t('createOffer.validation.aiWeightPositive'))
      .max(10, t('createOffer.validation.aiWeightMax')),
    niveau_etude: yup
      .number()
      .required()
      .min(0, t('createOffer.validation.aiWeightPositive'))
      .max(10, t('createOffer.validation.aiWeightMax')),
    languages: yup
      .number()
      .required()
      .min(0, t('createOffer.validation.aiWeightPositive'))
      .max(10, t('createOffer.validation.aiWeightMax')),
    certifications: yup
      .number()
      .required()
      .min(0, t('createOffer.validation.aiWeightPositive'))
      .max(10, t('createOffer.validation.aiWeightMax')),
    main_missions: yup
      .number()
      .required()
      .min(0, t('createOffer.validation.aiWeightPositive'))
      .max(10, t('createOffer.validation.aiWeightMax')),
    nice_to_have: yup
      .number()
      .required()
      .min(0, t('createOffer.validation.aiWeightPositive'))
      .max(10, t('createOffer.validation.aiWeightMax')),
  })
})

export type OfferFormData = yup.InferType<ReturnType<typeof getOfferFormSchema>>
